import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Circle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { guideCards, welcomeGuideHeading } from "./welcomeGuideCards";
import { useRedesignNav } from "@/lib/redesignNav";
import { WELCOME_GUIDE_COPY } from "@fomofi/shared";

/**
 * The welcome guide (BEN-141 page guidance, slice 2).
 *
 * Anthony's first mock, with the product decisions that were settled before this was
 * built. They are recorded here because each is a thing a future reader could otherwise
 * restore in good faith from the mock:
 *
 * 1. THE FIVE CARDS DESCRIBE THE PRODUCT JOURNEY, NOT WIZARD PROGRESS. They are numbered
 *    so a new user can see the order in which Fomo-Fi becomes useful, but the separate
 *    setup rail remains the source of truth for their current onboarding position.
 *    THE NUMBERS ARE FOR SETUP ONLY (X4, see rule 5). Anthony's mock numbers them, and
 *    the reason -- showing a newcomer the order -- holds only while there is an order
 *    still ahead of them. Do not restore the numbers after setup to match the mock.
 *
 * 2. THE CHECKLIST IS TWO ITEMS, NOT FOUR. The mock lists Connect accounts / Confirm
 *    income / Review recurring bills / Build first forecast. Only the first and last have
 *    a durable signal. `handleIncomeSourcesSave` issues NO request when the detected
 *    income was already correct, so "Confirm income" is indistinguishable from "never
 *    opened the screen" — and recurring rows are auto-detected, so their existence proves
 *    detection ran, not that a human reviewed them. A four-item list would sit at "2 of 4"
 *    forever for a user who had finished, which is a worse lie than omitting the rows.
 *    Same reasoning and same conclusion as §8's permanently-neutral rows in
 *    ConfirmContinueCard (BEN-141 4b item 4).
 *
 * 3. NO "unlock your full financial picture". The copy promises something a two-item
 *    checklist cannot deliver. On first entry, Start setup advances to the existing bank
 *    connection flow; on replay, Got it closes the guide. It is labelled "Start setup", not
 *    "Start tour": it starts the real setup, and calling it a tour sent a user into Plaid
 *    who believed they were still being shown around (BEN-141).
 *
 * 4. OVER SETUP, NOTHING IN IT IS A LINK. On first entry the dialog sits on top of the
 *    setup wizard, and the five cards were `<Link>`s to /banks, /financial-analysis and
 *    so on. For a user who hasn't finished setup every one bounces straight back to
 *    Welcome, so they behaved like a close button while looking like destinations.
 *    Anthony: "the steps are live action links, when clicked on, takes the user
 *    immediately from the setup process" (FOLLOWUP-001 F2). In setup they are plain,
 *    numbered steps, as on the Welcome step behind them. On replay, from Help or the
 *    dashboard, the user is onboarded and the pages are real, so the links stay.
 *    "In setup" is exactly "offers Start setup": only the wizard passes it.
 *
 * 5. AFTER SETUP IT IS NAVIGATION, NOT A TOUR (X4). The same box opens on the dashboard
 *    (first visit, WelcomeGuideHost) and from Help, for a user who has finished setup.
 *    It used to be dressed as a tour -- "Five steps take you from…", "Here's what will
 *    happen", cards numbered 1-5 -- while every card was a link out of it. That is
 *    Anthony's complaint verbatim: "within what i thought as the tour", "if we are going
 *    to provide a tour then it needs to be a tour". The pages are real for this user, so
 *    the links stay; they are named after the page they open, carry an arrow and a
 *    "Go to …" label, and nothing on the box calls itself a tour or a step. It does NOT
 *    offer the setup tour: that dialog's own copy ("Tour of setup", "Exit tour and go
 *    back to setup") is false for someone who has finished setup.
 *
 * WHAT "Build first forecast" ACTUALLY READS. `profile.onboarding_complete`, which
 * `ForecastGeneratingStep` writes at the end of the funnel — so the flag is literally set
 * when the first forecast is built. Note the consequence: OnboardingGate means anyone who
 * can reach an authed route already has it true, so in practice this row is a confirmation
 * and the only actionable row is Connect accounts. Reported rather than designed around,
 * because the alternative is a proxy signal.
 *
 * WHY THE BODY IS A SEPARATE EXPORT. Radix's Dialog renders through a portal and produces
 * NOTHING under `renderToStaticMarkup` — so a server-rendered assertion against the whole
 * dialog silently passes against an empty string. Three of this feature's most important
 * assertions are negative ("no Start Tour", "no unlock", "not numbered as steps"), and all
 * three would have been vacuous. The content is therefore its own component so the tests
 * can render the real thing without a DOM.
 */

export interface WelcomeGuideContentProps {
  /** Called on any dismissal — backdrop, Escape, the button, or following a link. */
  onDismiss: () => void;
  bankConnected: boolean;
  firstForecastBuilt: boolean;
  onStartSetup?: () => void;
  /** First entry only: open the setup tour, which describes the steps and
   *  starts nothing (see `lib/setupTour.ts`). */
  onTakeTour?: () => void;
  onSkip?: () => void;
}

export function WelcomeGuideContent({
  onDismiss,
  bankConnected,
  firstForecastBuilt,
  onStartSetup,
  onTakeTour,
  onSkip,
}: WelcomeGuideContentProps) {
  const cards = guideCards(useRedesignNav());
  // Rule 4: over setup, the cards and the checklist are steps, not destinations.
  const inSetup = onStartSetup !== undefined;

  const checklist = [
    {
      id: "connect-accounts",
      label: "Connect accounts",
      done: bankConnected,
      // The one row a user can still act on from here.
      to: bankConnected || inSetup ? null : "/banks",
    },
    { id: "first-forecast", label: "Build first forecast", done: firstForecastBuilt, to: null },
  ];
  const doneCount = checklist.filter((c) => c.done).length;

  return (
    <>
      <section className="mt-5" aria-labelledby="welcome-guide-pages">
        <h3
          id="welcome-guide-pages"
          className="mb-3 text-center text-[13px] font-semibold text-muted-foreground"
        >
          {inSetup ? WELCOME_GUIDE_COPY.setup.section : WELCOME_GUIDE_COPY.afterSetup.section}
        </h3>
        {/* Five across only once there is room: at 390px these stack, and the two-up band
            keeps each card wide enough for a two-line description. */}
        <ul
          role="list"
          className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
        >
          {cards.map(({ to, page, title, icon: Icon, description }, index) => {
            const icon = (
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-brand-accent/10">
                <Icon className="h-4 w-4 text-brand-accent" aria-hidden="true" />
              </span>
            );
            const blurb = (
              <span className="text-[12px] leading-snug text-muted-foreground">{description}</span>
            );
            return (
              <li key={to}>
                {inSetup ? (
                  // Rule 4: a numbered step. No hover, no pointer, no border: nothing
                  // here says "click me".
                  <div className="flex h-full flex-col gap-1.5 rounded-xl bg-muted/40 p-3">
                    <span className="flex items-center gap-2">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-accent-fill text-xs font-bold text-brand-accent-fill-foreground">
                        {index + 1}
                      </span>
                      {icon}
                      <span className="text-[13px] font-semibold text-foreground">{title}</span>
                    </span>
                    {blurb}
                  </div>
                ) : (
                  // Rule 5: a destination, named after the page, with no step number.
                  <Link
                    to={to}
                    onClick={onDismiss}
                    aria-label={`Go to ${page}`}
                    className="group flex h-full flex-col gap-1.5 rounded-xl border border-border bg-card p-3 transition hover:border-brand-accent/40 hover:bg-accent"
                  >
                    <span className="flex items-center gap-2">
                      {icon}
                      <span className="min-w-0 flex-1 text-[13px] font-semibold text-foreground">
                        {page}
                      </span>
                      <ArrowRight
                        className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-foreground"
                        aria-hidden="true"
                      />
                    </span>
                    {blurb}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {/* Inside setup the checklist is a third count ("0 of 2") beside the rail's
          "Step 1 of 8" and the intro's step count (2026-09-25 walkthrough). The
          rail is the progress there, so the checklist shows only after setup. */}
      <div className={`mt-5 grid grid-cols-1 gap-3 ${inSetup ? "" : "md:grid-cols-2"}`}>
        {!inSetup && (
          <section
            className="rounded-xl border border-border bg-card p-4"
            aria-labelledby="welcome-guide-checklist"
          >
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <h3
                id="welcome-guide-checklist"
                className="text-[13px] font-semibold text-foreground"
              >
                Let&rsquo;s get you set up
              </h3>
              <p className="text-[12px] tabular-nums text-muted-foreground">
                {doneCount} of {checklist.length} completed
              </p>
            </div>
            <ul role="list" className="space-y-1.5">
              {checklist.map(({ id, label, done, to }) => (
                <li key={id} className="flex items-center gap-2">
                  {done ? (
                    <CheckCircle2
                      className="h-4 w-4 flex-shrink-0"
                      style={{ color: "var(--success)" }}
                      strokeWidth={2.5}
                      aria-label="Done"
                    />
                  ) : (
                    // Neutral, matching §8's language: nothing is wrong, it is simply not
                    // done yet.
                    <Circle
                      className="h-4 w-4 flex-shrink-0 text-muted-foreground/50"
                      strokeWidth={2}
                      aria-label="Not done yet"
                    />
                  )}
                  {to ? (
                    <Link
                      to={to}
                      onClick={onDismiss}
                      className="text-[12px] font-medium text-foreground underline decoration-border underline-offset-2 hover:decoration-foreground"
                    >
                      {label}
                    </Link>
                  ) : (
                    <span className="text-[12px] text-foreground">{label}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="flex gap-3 rounded-xl bg-brand-accent/10 p-4">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-accent">
            <Sparkles className="h-4 w-4 text-white" aria-hidden="true" />
          </span>
          <div>
            <p className="text-[13px] font-semibold text-foreground">You&rsquo;re in control.</p>
            <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
              We&rsquo;ll show you insights, but you make the decisions. Every step brings you
              closer to financial clarity.
            </p>
          </div>
        </section>
      </div>

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onSkip && (
          <Button type="button" variant="ghost" onClick={onSkip}>
            Skip for now
          </Button>
        )}
        {onTakeTour && (
          <Button type="button" variant="outline" onClick={onTakeTour}>
            Take the tour
          </Button>
        )}
        <Button
          type="button"
          onClick={onStartSetup ?? onDismiss}
          className="bg-brand-accent-fill text-brand-accent-fill-foreground hover:opacity-90"
        >
          {onStartSetup ? "Start setup" : "Got it"}
        </Button>
      </div>
    </>
  );
}

interface WelcomeGuideDialogProps extends WelcomeGuideContentProps {
  open: boolean;
}

export function WelcomeGuideDialog({ open, ...content }: WelcomeGuideDialogProps) {
  const inSetup = content.onStartSetup !== undefined;
  const heading = welcomeGuideHeading(inSetup);
  return (
    <Dialog open={open} onOpenChange={(next) => !next && content.onDismiss()}>
      {/* Title and description are the accessible name/description, so no sr-only block. */}
      <DialogContent
        className="max-h-[85vh] overflow-y-auto p-6 sm:max-w-4xl"
        // Over setup (rule 4) nothing before the buttons is focusable, so Radix's
        // default first focus fell on "Skip for now": one Enter from skipping setup.
        // Focus the dialog itself; Tab still reaches every button.
        onOpenAutoFocus={
          inSetup
            ? (event) => {
                event.preventDefault();
                (event.currentTarget as HTMLElement | null)?.focus();
              }
            : undefined
        }
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-[22px] font-bold">{heading.title}</DialogTitle>
          <DialogDescription className="text-[13px]">{heading.description}</DialogDescription>
        </DialogHeader>
        <WelcomeGuideContent {...content} />
      </DialogContent>
    </Dialog>
  );
}
